import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Calculator, 
  TrendingUp, 
  Shield, 
  Wallet, 
  AlertCircle, 
  CheckCircle2, 
  DollarSign, 
  PiggyBank, 
  Heart, 
  Target,
  TrendingDown,
  Home,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FinancialPlanner({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [latestPlan, setLatestPlan] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    age: '',
    monthly_income: '',
    monthly_emi: '',
    monthly_expenses: '',
    emergency_fund: '',
    has_health_insurance: 'no',
    has_term_insurance: 'no'
  });

  useEffect(() => {
    fetchLatestPlan();
  }, []);

  const fetchLatestPlan = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/financial-plan/latest`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const plan = response.data;
      setLatestPlan(plan);
      
      // Pre-populate form with the values from the existing plan
      if (plan) {
        setFormData({
          first_name: plan.first_name || '',
          last_name: plan.last_name || '',
          age: plan.age?.toString() || '',
          monthly_income: plan.monthly_income?.toString() || '',
          monthly_emi: plan.monthly_emi?.toString() || '',
          monthly_expenses: plan.monthly_expenses?.toString() || '',
          emergency_fund: plan.emergency_fund?.toString() || '',
          has_health_insurance: plan.has_health_insurance ? 'yes' : 'no',
          has_term_insurance: plan.has_term_insurance ? 'yes' : 'no'
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching plan:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        age: parseInt(formData.age),
        monthly_income: parseFloat(formData.monthly_income),
        monthly_emi: parseFloat(formData.monthly_emi),
        monthly_expenses: parseFloat(formData.monthly_expenses),
        emergency_fund: parseFloat(formData.emergency_fund),
        has_health_insurance: formData.has_health_insurance === 'yes',
        has_term_insurance: formData.has_term_insurance === 'yes'
      };

      const response = await axios.post(`${API}/financial-plan`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLatestPlan(response.data);
      toast.success('Financial plan generated successfully!');
      
      // Scroll to plan smoothly
      setTimeout(() => {
        document.getElementById('generated-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      console.error('Error generating plan:', error);
      toast.error('Failed to generate financial plan');
    } finally {
      setGenerating(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 60) return 'text-blue-600 dark:text-blue-400';
    if (score >= 40) return 'text-amber-600 dark:text-amber-400';
    return 'text-rose-600 dark:text-rose-400';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  // Parse AI recommendations into separate sections
  const parseRecommendationSections = (text) => {
    if (!text) return [];
    
    const sections = [];
    const sectionRegex = /###\s*(\d+)\.\s*\*\*(.*?)\*\*/g;
    const matches = [...text.matchAll(sectionRegex)];
    
    matches.forEach((match, index) => {
      const sectionNumber = match[1];
      const sectionTitle = match[2];
      const startIndex = match.index + match[0].length;
      const endIndex = matches[index + 1] ? matches[index + 1].index : text.length;
      const content = text.substring(startIndex, endIndex).trim();
      
      // Parse bullet points
      const bullets = content.split('\n')
        .filter(line => line.trim().startsWith('-'))
        .map(line => line.trim().replace(/^-\s*/, '').replace(/\*\*/g, ''));
      
      // Get remaining text (non-bullet points)
      const description = content.split('\n')
        .filter(line => !line.trim().startsWith('-') && !line.trim().startsWith('#'))
        .join(' ')
        .trim()
        .replace(/\*\*/g, '');
      
      sections.push({
        number: sectionNumber,
        title: sectionTitle,
        description,
        bullets
      });
    });
    
    return sections;
  };

  // Get color for each section
  const getSectionColor = (index) => {
    const colors = [
      { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-800 dark:text-yellow-200' },
      { bg: 'bg-cyan-50 dark:bg-cyan-900/20', border: 'border-cyan-200 dark:border-cyan-800', text: 'text-cyan-800 dark:text-cyan-200' },
      { bg: 'bg-pink-50 dark:bg-pink-900/20', border: 'border-pink-200 dark:border-pink-800', text: 'text-pink-800 dark:text-pink-200' },
      { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-800 dark:text-green-200' },
      { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-800 dark:text-blue-200' },
      { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', text: 'text-purple-800 dark:text-purple-200' },
      { bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-800 dark:text-orange-200' }
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8 dark:bg-gray-900">
        <Navigation user={user} onLogout={onLogout} currentPage="/financial-planner" />
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 dark:bg-gray-900">
      <Navigation user={user} onLogout={onLogout} currentPage="/financial-planner" />

      <div className="max-w-6xl mx-auto">
        <>
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">Generate Your Financial Plan</h1>
            <p className="text-gray-600 dark:text-gray-400">Get a comprehensive financial plan powered by AI</p>
          </div>

          {/* Input Form - Centered with limited width */}
          <div className="max-w-2xl mx-auto mb-12">
          <Card className="p-6 glass dark:bg-gray-800">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-6">Enter Your Details</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="dark:text-gray-300">First Name</Label>
                  <Input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="John"
                    required
                  />
                </div>
                <div>
                  <Label className="dark:text-gray-300">Last Name</Label>
                  <Input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="dark:text-gray-300">Age</Label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  placeholder="30"
                  required
                  min="18"
                  max="100"
                />
              </div>
              <div>
                <Label className="dark:text-gray-300">Income - Monthly</Label>
                <Input
                  type="number"
                  value={formData.monthly_income}
                  onChange={(e) => setFormData({ ...formData, monthly_income: e.target.value })}
                  placeholder="e.g., 100000"
                  required
                  step="0.01"
                />
              </div>
              <div>
                <Label className="dark:text-gray-300">EMIs - Monthly</Label>
                <Input
                  type="number"
                  value={formData.monthly_emi}
                  onChange={(e) => setFormData({ ...formData, monthly_emi: e.target.value })}
                  placeholder="e.g., 25000"
                  required
                  step="0.01"
                />
              </div>
              <div>
                <Label className="dark:text-gray-300">Expenses - Monthly</Label>
                <Input
                  type="number"
                  value={formData.monthly_expenses}
                  onChange={(e) => setFormData({ ...formData, monthly_expenses: e.target.value })}
                  placeholder="e.g., 40000"
                  required
                  step="0.01"
                />
              </div>
              <div>
                <Label className="dark:text-gray-300">Emergency Fund (Total Savings)</Label>
                <Input
                  type="number"
                  value={formData.emergency_fund}
                  onChange={(e) => setFormData({ ...formData, emergency_fund: e.target.value })}
                  placeholder="e.g., 500000"
                  required
                  step="0.01"
                />
              </div>
              <div>
                <Label className="dark:text-gray-300">Do you have a Health Insurance policy?</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="health_insurance"
                      value="yes"
                      checked={formData.has_health_insurance === 'yes'}
                      onChange={(e) => setFormData({ ...formData, has_health_insurance: e.target.value })}
                      className="w-4 h-4 text-teal-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">Yes</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="health_insurance"
                      value="no"
                      checked={formData.has_health_insurance === 'no'}
                      onChange={(e) => setFormData({ ...formData, has_health_insurance: e.target.value })}
                      className="w-4 h-4 text-teal-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">No</span>
                  </label>
                </div>
              </div>
              <div>
                <Label className="dark:text-gray-300">Do you have a Term Insurance policy?</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="term_insurance"
                      value="yes"
                      checked={formData.has_term_insurance === 'yes'}
                      onChange={(e) => setFormData({ ...formData, has_term_insurance: e.target.value })}
                      className="w-4 h-4 text-teal-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">Yes</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="term_insurance"
                      value="no"
                      checked={formData.has_term_insurance === 'no'}
                      onChange={(e) => setFormData({ ...formData, has_term_insurance: e.target.value })}
                      className="w-4 h-4 text-teal-600"
                    />
                    <span className="text-gray-700 dark:text-gray-300">No</span>
                  </label>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-teal-500 to-blue-600 hover:from-teal-600 hover:to-blue-700"
                disabled={generating}
              >
                {generating ? (
                  <>
                    <Calculator className="mr-2 h-4 w-4 animate-spin" />
                    Generating Plan...
                  </>
                ) : (
                  <>
                    <Calculator className="mr-2 h-4 w-4" />
                    Generate Financial Plan
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>

        {/* Generated Plan - Full Width within container */}
        {latestPlan && (
          <div id="generated-plan" className="space-y-6">
              {/* Header with Name */}
              <div className="text-center mb-8">
                <h2 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                  Financial Plan for {latestPlan.first_name} {latestPlan.last_name}
                </h2>
                <p className="text-gray-600 dark:text-gray-400">Age: {latestPlan.age} | Generated on {new Date(latestPlan.created_at).toLocaleDateString()}</p>
              </div>

              {/* Summary Snapshot */}
              <Card className="p-8 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 border-2 border-yellow-200 dark:border-yellow-800">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center">
                  <span className="mr-2 text-3xl">📊</span>
                  Summary Snapshot
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Financial Health</p>
                    <p className="text-3xl font-bold text-teal-600 dark:text-teal-400">{latestPlan.health_score}/100</p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{getScoreLabel(latestPlan.health_score)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Monthly Surplus</p>
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(latestPlan.plan_details.monthly_surplus, user?.currency)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Savings Rate</p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{latestPlan.plan_details.savings_rate}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Years to Retirement</p>
                    <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{latestPlan.plan_details.years_to_retirement}</p>
                  </div>
                </div>
              </Card>

              {/* Your Entered Details */}
              <Card className="p-8 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-2 border-blue-200 dark:border-blue-800">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center">
                  <span className="mr-2 text-3xl">📋</span>
                  1. Your Entered Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Monthly Income:</span>
                    <span className="font-bold text-gray-900 dark:text-gray-100">{formatCurrency(latestPlan.monthly_income, user?.currency)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Monthly EMI:</span>
                    <span className="font-bold text-gray-900 dark:text-gray-100">{formatCurrency(latestPlan.monthly_emi, user?.currency)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Monthly Expenses:</span>
                    <span className="font-bold text-gray-900 dark:text-gray-100">{formatCurrency(latestPlan.monthly_expenses, user?.currency)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Emergency Fund:</span>
                    <span className="font-bold text-gray-900 dark:text-gray-100">{formatCurrency(latestPlan.emergency_fund, user?.currency)}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Health Insurance:</span>
                    <span className="font-bold text-gray-900 dark:text-gray-100">{latestPlan.has_health_insurance ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">Term Insurance:</span>
                    <span className="font-bold text-gray-900 dark:text-gray-100">{latestPlan.has_term_insurance ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </Card>

              {/* AI Recommendations - Individual Colored Sections */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center">
                  <span className="mr-2 text-3xl">💡</span>
                  2. Your Personalised Financial Plan
                </h3>
                
                {parseRecommendationSections(latestPlan.ai_recommendations).map((section, index) => {
                  const colors = getSectionColor(index);
                  return (
                    <Card 
                      key={index}
                      className={`p-6 ${colors.bg} border-2 ${colors.border}`}
                    >
                      <h4 className={`text-xl font-bold mb-4 ${colors.text}`}>
                        {section.number}. {section.title}
                      </h4>
                      
                      {section.description && (
                        <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                          {section.description}
                        </p>
                      )}
                      
                      {section.bullets.length > 0 && (
                        <ul className="space-y-2">
                          {section.bullets.map((bullet, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <span className={`mt-1 ${colors.text}`}>•</span>
                              <span className="text-gray-700 dark:text-gray-300 leading-relaxed flex-1">
                                {bullet}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Card>
                  );
                })}
              </div>

              {/* Regenerate Button */}
              <div className="text-center pt-6">
                <Button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  variant="outline"
                  size="lg"
                  className="dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600"
                >
                  Generate New Plan
                </Button>
              </div>
            </div>
          )}
          <Footer />
        </>
      </div>
    </div>
  );
}
